import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { X, AlertTriangle } from "lucide-react";

export default function RefundDialog({ booking, onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [mode, setMode] = useState("auto");
  const [customAmount, setCustomAmount] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!booking) return;
    api
      .get(`/admin/bookings/${booking.id}/refund-preview`)
      .then((r) => {
        setPreview(r.data);
        const tier = r.data.policy.tier;
        setMode(tier === "none" ? "custom" : "auto");
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Preview failed"));
  }, [booking]);

  if (!booking) return null;

  const total = Number(booking.total_amount || 0);
  const policy = preview?.policy;

  const computeAmount = () => {
    if (mode === "auto") return policy?.amount ?? 0;
    if (mode === "full") return total;
    if (mode === "half") return total * 0.5;
    if (mode === "custom") return Number(customAmount || 0);
    return 0;
  };

  const submit = async () => {
    const amount = computeAmount();
    if (mode === "custom" && (amount <= 0 || amount > total)) {
      toast.error("Custom amount must be > 0 and ≤ total");
      return;
    }
    const conf = window.confirm(
      `Επιβεβαίωση επιστροφής €${amount.toFixed(2)} στον/στην ${booking.guest_name};\n\nConfirm refund of €${amount.toFixed(2)} to ${booking.guest_name}?`
    );
    if (!conf) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/bookings/${booking.id}/refund`, {
        mode,
        ...(mode === "custom" ? { amount } : {}),
      });
      toast.success(`Refund: €${r.data.amount} (${r.data.refund_status || "skipped"})`);
      onDone?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refund failed");
    } finally {
      setBusy(false);
    }
  };

  const RadioRow = ({ value, title, sub }) => (
    <label
      className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
        mode === value
          ? "border-[#1A2B4C] bg-[#F5F2EB]"
          : "border-[#E9E2D3] hover:border-[#1A2B4C]/50"
      }`}
    >
      <input
        type="radio"
        name="refund-mode"
        value={value}
        checked={mode === value}
        onChange={() => setMode(value)}
        className="mt-1"
        data-testid={`refund-mode-${value}`}
      />
      <div className="flex-1">
        <div className="text-sm text-[#1A2B4C] font-medium">{title}</div>
        {sub && <div className="text-xs text-[#6B705C] mt-0.5">{sub}</div>}
      </div>
    </label>
  );

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl max-w-xl w-full p-6 sm:p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#6B705C]">Refund</p>
            <h2 className="font-serif text-2xl text-[#1A2B4C] mt-1">
              {booking.guest_name}
            </h2>
            <p className="text-xs text-[#6B705C] mt-1">
              {booking.apartment_name} · {booking.check_in} → {booking.check_out} · €{total}
            </p>
          </div>
          <button onClick={onClose} className="text-[#6B705C] hover:text-[#1A2B4C] p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Policy preview */}
        {policy && (
          <div className="bg-[#F5F2EB] rounded-xl p-4 mb-5">
            <div className="flex items-start gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-[#6B705C] mt-0.5" />
              <p className="text-xs uppercase tracking-widest text-[#6B705C]">
                Cancellation policy
              </p>
            </div>
            <p className="text-sm text-[#1A2B4C]">{policy.reason_el}</p>
            <p className="text-xs text-[#6B705C] mt-1">{policy.reason_en}</p>
            <p className="font-serif text-3xl text-[#1A2B4C] mt-3">
              €{policy.amount.toFixed(2)}{" "}
              <span className="text-sm font-sans text-[#6B705C]">({policy.percent}%)</span>
            </p>
          </div>
        )}

        {/* Override options */}
        <p className="text-xs uppercase tracking-widest text-[#6B705C] mb-3">
          Επιστροφή / Refund mode
        </p>
        <div className="space-y-2 mb-4">
          <RadioRow
            value="auto"
            title={`Αυτόματο βάσει πολιτικής — €${(policy?.amount ?? 0).toFixed(2)}`}
            sub="Apply policy automatically"
          />
          <RadioRow
            value="full"
            title={`Πλήρης επιστροφή — €${total.toFixed(2)}`}
            sub="100% override"
          />
          <RadioRow
            value="half"
            title={`50% επιστροφή — €${(total * 0.5).toFixed(2)}`}
            sub="50% override"
          />
          <RadioRow
            value="custom"
            title="Custom amount"
            sub="Set a specific refund amount"
          />
          {mode === "custom" && (
            <div className="ml-7 mt-2">
              <input
                type="number"
                step="0.01"
                min="0"
                max={total}
                placeholder={`Max €${total}`}
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                data-testid="refund-custom-amount"
                className="w-40 bg-[#F5F2EB] border border-[#E9E2D3] rounded-xl px-3 py-2 text-[#1A2B4C] focus:outline-none focus:ring-2 focus:ring-[#1A2B4C]/30 text-sm"
              />
            </div>
          )}
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-5 text-xs text-amber-800">
          ⚠️ Η κράτηση θα ακυρωθεί και θα σταλεί email στον πελάτη.
          The booking will be cancelled and an email will be sent to the guest.
        </div>

        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            disabled={busy}
            className="px-5 py-2.5 rounded-full text-sm text-[#6B705C] border border-[#E9E2D3] hover:bg-[#F5F2EB]"
          >
            Άκυρο
          </button>
          <button
            onClick={submit}
            disabled={busy}
            data-testid="refund-submit"
            className="bg-[#1A2B4C] text-white px-6 py-2.5 rounded-full text-sm uppercase tracking-widest hover:-translate-y-0.5 transition-transform disabled:opacity-50"
          >
            {busy ? "Processing..." : `Refund €${computeAmount().toFixed(2)}`}
          </button>
        </div>
      </div>
    </div>
  );
}
